import { Button, Grid, makeStyles, Paper, Typography, IconButton, useMediaQuery, SvgIcon } from '@material-ui/core'
import useTranslate from '../../../../../i18n/useTranslate'
import { useTheme } from '@material-ui/core/styles'
import EditIcon from '@material-ui/icons/Edit'
import { Delete } from '@material-ui/icons'
import { ReactComponent as DocumentIcon } from '../../../../../images/document.svg'
import { ResultSearchType } from '../../../../../services/manageExternalProject/searchFormExternalProjectType'
import useManageExternalProject from '../../../../../services/manageExternalProject/useManageExternalProject'
import { FC, useRef, useState } from 'react'
import { convertDateToFormatEN, convertDateToFormatTH } from '../../../../../utilities/date'
import Dialog, { DialogType } from '../../../../../control/dialog/dialog'
import Tooltip from '../../../../../control/tooltip/tooltip'
import { GridRowData } from '@material-ui/data-grid'
import { generatePath, useHistory } from 'react-router-dom'
import { renderMode } from '../../../../../constants/constants'
import { encryptionAES } from '../../../../../utilities/encryption'
import { RoutePath } from '../../../../../router/routesConfig'
import useLoading from '../../../../../control/loading/useLoading'

interface ItemPropsExternal {
    dataItem: ResultSearchType
    getList: (projectID?: number) => void
}

const ItemExternalProject: FC<ItemPropsExternal> = ({ dataItem, getList }) => {
    const translate = useTranslate()
    const classes = useStyles()
    const theme = useTheme()
    const { deleteProject } = useManageExternalProject()
    const [configDialog, setConfigDialog] =
        useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogTwoRef = useRef<Record<string, Function>>()
    const history = useHistory()
    const loadingHook = useLoading()

    const isMobileView = useMediaQuery(theme.breakpoints.down('sm'))

    const onRefDialogTwo = (func: Record<string, Function>) => {
        dialogTwoRef.current = func
    }
    const handleDelete = async () => {
        setConfigDialog({
            title: translate.get('ALERT.TITLE.WARNING'),
            content: translate.get('ALERT.CONTENT.NEED_TO_DELETE'),
            type: 'alert-confirm',

            onClickConfirm: async () => {
                loadingHook.loadingOpen()
                const response = await deleteProject(dataItem.projectID)
                if (response.result.status === 200) {
                    setConfigDialog({
                        title: translate.get('ALERT.TITLE.NOTIFICATION'),
                        content: translate.get('ALERT.CONTENT.DELETE_SUCCESS'),
                        type: 'alert-message',
                        onAfterClose: () => {
                            getList(dataItem.projectID)
                        },
                    })
                    loadingHook.loadingClose()
                } else {
                    setConfigDialog({
                        title: translate.get('ALERT.TITLE.NOTIFICATION'),
                        content: translate.get('ALERT.CONTENT.DELETE_UNSUCCESS'),
                        type: 'alert-message',
                    })
                }
            },
        })
        dialogTwoRef.current?.openDialog()
    }

    const handleClickEdit = (row: GridRowData) => {
        const encryptedProjectID = encryptionAES(row.projectID)
        const idDraft = row.is_draft === 1 ? true : false

        const path = generatePath(RoutePath.manageExternalProjectFormEdit, {
            projectID: encryptedProjectID,
        })

        if (idDraft) {
            history.push({
                pathname: path,
                state: {
                    irb: row.irb,
                    mode: renderMode.edit,
                    data: dataItem,
                },
            })
        } else {
            history.push({
                pathname: path,
                state: {
                    irb: row.irb,
                    mode: renderMode.view,
                    data: dataItem,
                },
            })
        }
    }

    const itemExternalProject = () => {
        return (
            <>
                <Paper className={classes.paper} variant="outlined">
                    <Grid container spacing={1} justify="space-between">
                        <Grid container xs={9}>
                            <Button
                                color="default"
                                variant="contained"
                                className={classes.btnEdit}
                                onClick={handleClickEdit}
                            >
                                {translate.get('BUTTON.EDIT')}
                            </Button>
                            <Button variant="contained" className={classes.btnDelete} onClick={handleDelete}>
                                {translate.get('BUTTON.DELETE')}
                            </Button>

                            <Grid container item xs={3} alignItems="center">
                                <Grid item xs={12}>
                                    <Typography className={classes.titleInCard} noWrap variant={'caption'}>
                                        {translate.get('LABEL.COA_NO')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography className={classes.coa} noWrap variant={'body2'}>
                                        {dataItem.certificateID}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Grid container item xs={4} alignItems="center">
                                <Grid item xs={12}>
                                    <Typography className={classes.titleInCard} noWrap variant={'caption'}>
                                        {translate.get('LABEL.RESEARCHER_NAME')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography noWrap variant={'body2'}>
                                        <Tooltip
                                            title={
                                                translate.languageStore.currentLanguage === translate.languageKey.th
                                                    ? dataItem.projectNameTh
                                                    : dataItem.projectNameEn
                                            }
                                        >
                                            <IconButton size="small" style={{ padding: '0px' }}>
                                                <SvgIcon fontSize="small" component={DocumentIcon} />
                                            </IconButton>
                                        </Tooltip>
                                        &nbsp;{' '}
                                        <Tooltip
                                            title={
                                                translate.languageStore.currentLanguage === translate.languageKey.th
                                                    ? dataItem.fullnameTH
                                                    : dataItem.fullnameEN
                                            }
                                        >
                                            {translate.languageStore.currentLanguage === translate.languageKey.th
                                                ? dataItem.fullnameTH
                                                : dataItem.fullnameEN}
                                        </Tooltip>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid container item xs={3} alignItems="center">
                            <Grid item xs={12} className={classes.certificateDateTitle}>
                                <Typography noWrap variant={'caption'}>
                                    {translate.get('LABEL.EXTERNAL_PROJECT_CREATION_DATE')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} className={classes.certificateDate}>
                                <Typography noWrap variant={'body2'}>
                                    {translate.languageStore.currentLanguage === translate.languageKey.en
                                        ? convertDateToFormatEN(dataItem.certificateDate)
                                        : convertDateToFormatTH(dataItem.certificateDate)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>

                <Dialog {...configDialog} onRef={onRefDialogTwo} />
            </>
        )
    }

    const itemExternalProjectMobileView = () => {
        return (
            <>
                <Paper className={classes.paper} variant="outlined">
                    <Grid container xs={12} spacing={2} justify="space-between">
                        <Grid container xs={9}>
                            <IconButton aria-label="InfoIcon" onClick={handleClickEdit}>
                                <EditIcon fontSize={'small'} />
                            </IconButton>
                            <IconButton aria-label="InfoIcon" onClick={handleDelete}>
                                <Delete fontSize={'small'} color="secondary" />
                            </IconButton>

                            <Grid container item xs={6} alignItems="center" className={classes.boxMobile}>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <Typography className={classes.titleInCard} noWrap variant={'caption'}>
                                            {translate.get('LABEL.COA_NO')}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography className={classes.coa} noWrap variant={'body2'}>
                                            {dataItem.certificateID}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography noWrap variant={'body2'}>
                                        <Tooltip
                                            title={
                                                translate.languageStore.currentLanguage === translate.languageKey.th
                                                    ? dataItem.fullnameTH
                                                    : dataItem.fullnameEN
                                            }
                                        >
                                            {translate.languageStore.currentLanguage === translate.languageKey.th
                                                ? dataItem.fullnameTH
                                                : dataItem.fullnameEN}
                                        </Tooltip>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid container item xs={3} alignItems="center">
                            <Grid item xs={12} className={classes.certificateDateTitleMobile}>
                                <Tooltip title={translate.get('LABEL.EXTERNAL_PROJECT_CREATION_DATE')}>
                                    <Typography noWrap variant={'caption'}>
                                        {translate.get('LABEL.EXTERNAL_PROJECT_CREATION_DATE')}
                                    </Typography>
                                </Tooltip>
                            </Grid>

                            <Grid item xs={12} className={classes.certificateDate}>
                                <Typography noWrap variant={'body2'}>
                                    <Tooltip
                                        title={
                                            translate.languageStore.currentLanguage === translate.languageKey.en
                                                ? convertDateToFormatEN(dataItem.certificateDate)
                                                : convertDateToFormatTH(dataItem.certificateDate)
                                        }
                                    >
                                        {translate.languageStore.currentLanguage === translate.languageKey.en
                                            ? convertDateToFormatEN(dataItem.certificateDate)
                                            : convertDateToFormatTH(dataItem.certificateDate)}
                                    </Tooltip>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>

                <Dialog {...configDialog} onRef={onRefDialogTwo} />
            </>
        )
    }

    return isMobileView ? itemExternalProjectMobileView() : itemExternalProject()
}

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: '24px',
        marginBottom: '10px',
        marginRight: '0px',
        transition: '0.3s',
        borderRadius: '4px',
        position: 'relative',
        '&:hover': {
            border: `1px solid ${theme.palette.primary.main}`,
        },
    },
    btnEdit: {
        height: '44px',
        minWidth: '0px',
        borderRadius: '4px',
        boxShadow: `0px 4px 4px ${theme.palette.grey['50']}`,
        textAlign: 'center',
        marginRight: '10px',
        fontSize: '16px',
    },
    btnDelete: {
        height: '44px',
        minWidth: '0px',
        width: '64px',
        borderRadius: '4px',
        boxShadow: `0px 4px 4px ${theme.palette.grey['50']}`,
        textAlign: 'center',
        color: theme.palette.background.default,
        marginRight: '6vw',
        backgroundColor: theme.palette.secondary.main,
        fontSize: '16px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    btnPopover: {
        minWidth: '0px',
        padding: '0px',
        backgroundColor: theme.palette.common.white,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: theme.palette.background.default,
            boxShadow: 'none',
        },
    },
    titleInCard: {
        border: 'unset',
        color: theme.palette.text.secondary,
        fontWeight: 400,
    },
    certificateDateTitle: {
        border: 'unset',
        color: theme.palette.text.secondary,
        fontWeight: 400,
        textAlign: 'end',
    },
    certificateDateTitleMobile: {
        border: 'unset',
        color: theme.palette.text.secondary,
        fontWeight: 400,
        textAlign: 'end',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    certificateDate: {
        textAlign: 'center',
        marginLeft: '25px',
    },
    coa: {
        border: 'unset',
        paddingLeft: '0px',
    },
    boxMobile: {
        marginLeft: '15px',
        textAlign: 'center',
    },
}))

export default ItemExternalProject
